import * as React from 'react'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import { Box, FormControlLabel, Checkbox, Typography } from '@mui/material'
import { useChangelist } from './hooks'
import {
  edgateSubjectAtom,
  edgateStandardsAtom,
  kiddomStandardsAtom,
  changelistAtom,
} from './atoms'
import { renderLoading, renderMessage } from './utility'
import { ChangelistView } from './ChangelistView'
import { ApplyChangelist } from './ApplyChangelist'

interface ChangelistProps {
  state: string
  subject: string
}

export const Changelist: React.FC<ChangelistProps> = ({ state, subject }) => {
  const { loadingChangelist, fetchChangelist } = useChangelist()
  const [edgateSubject] = useAtom(edgateSubjectAtom)
  const [edgateStandards] = useAtom(edgateStandardsAtom)
  const [kiddomStandards] = useAtom(kiddomStandardsAtom)
  const [changelist, setChangelist] = useAtom(changelistAtom)
  const [traceParents, setTraceParents] = React.useState(false)

  // Clear data when state or subject changes
  React.useEffect(() => {
    setChangelist(null)
  }, [state, subject, setChangelist])

  // Fetch changelist when all required data is available
  React.useEffect(() => {
    if (
      state &&
      subject &&
      edgateSubject &&
      edgateStandards &&
      kiddomStandards &&
      !changelist &&
      !loadingChangelist
    ) {
      fetchChangelist({
        state,
        kiddom_subject: subject,
        edgate_subject: edgateSubject,
        trace_parents: traceParents,
      }).then((result) => {
        if (result) {
          setChangelist(result)
        }
      })
    }
  }, [
    state,
    subject,
    edgateSubject,
    edgateStandards,
    kiddomStandards,
    changelist,
    loadingChangelist,
    traceParents,
    fetchChangelist,
    setChangelist,
  ])

  // Reset trace_parents when state changes
  React.useEffect(() => {
    setTraceParents(state === 'VA')
  }, [state])

  // Check if all required parameters are selected
  if (!state || !subject) {
    return renderMessage('Select state and subject')
  }

  // Check if changelist is loading or not loaded yet
  if (!changelist) {
    return renderLoading(`Loading changelist for ${state} ${subject}`)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={traceParents}
            onChange={(e) => setTraceParents(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            Trace Parents
          </Typography>
        }
      />
      <ChangelistView
        state={state}
        subject={subject}
        edgateSubject={edgateSubject}
      />
      <ApplyChangelist changelist={changelist} />
    </Box>
  )
}
